import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  Activity, BookOpen, Boxes, FileBarChart, LayoutDashboard, LogOut, Map, Menu, MessageSquareCode,
  Shield, Terminal, User as UserIcon, Wrench, X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import { TrialBanner } from "@/components/TrialBanner";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
  { to: "/roadmap", label: "My Roadmap", icon: Map, id: "roadmap" },
  { to: "/learn", label: "Learn", icon: BookOpen, id: "learn" },
  { to: "/practice", label: "Practice", icon: Terminal, id: "practice" },
  { to: "/labs", label: "Labs", icon: Wrench, id: "labs" },
  { to: "/projects", label: "Projects", icon: Boxes, id: "projects" },
  { to: "/interview", label: "Interview", icon: MessageSquareCode, id: "interview" },
  { to: "/reports", label: "Reports", icon: FileBarChart, id: "reports" },
  { to: "/profile", label: "Profile", icon: UserIcon, id: "profile" },
];
export const AppShell = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const isAdmin = ["platform_admin", "content_admin"].includes(user?.role);

  const links = (suffix = "") => (
    <nav className="flex flex-col gap-0.5">
      {NAV.map(({ to, label, icon: Icon, id }) => (
        <NavLink
          key={to}
          to={to}
          data-testid={`nav-${id}${suffix}`}
          onClick={() => setOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-2.5 text-sm border-l-2 transition-colors duration-150 ${
              isActive
                ? "border-primary bg-primary/5 text-foreground font-medium"
                : "border-transparent text-muted-foreground hover:text-foreground hover:bg-accent/60"
            }`
          }
        >
          <Icon className="h-4 w-4" />
          {label}
        </NavLink>
      ))}
      {isAdmin && (
        <NavLink
          to="/admin"
          data-testid={`nav-admin${suffix}`}
          onClick={() => setOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-2.5 text-sm border-l-2 mt-4 transition-colors duration-150 ${
              isActive ? "border-primary bg-primary/5 text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
            }`
          }
        >
          <Shield className="h-4 w-4" />
          Admin
        </NavLink>
      )}
    </nav>
  );

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="hidden lg:flex w-60 shrink-0 flex-col border-r border-border sticky top-0 h-screen">
        <Link to="/dashboard" className="flex items-center gap-2 px-4 h-16 border-b border-border">
          <Activity className="h-4 w-4 text-primary" />
          <span className="font-display font-bold tracking-tight">DevOps Mentor</span>
        </Link>
        <div className="py-4 flex-1 overflow-y-auto">{links()}</div>
        <div className="border-t border-border p-4">
          <p className="text-xs text-muted-foreground truncate" data-testid="sidebar-user-email">{user?.email}</p>
          <p className="overline text-primary mt-1">
            {user?.access?.isTrial ? `trial · ${user.access.trialDaysLeft}d left` : `${user?.access?.effectivePlan || user?.planSlug || "free"} plan`}
          </p>
          <button
            data-testid="logout-btn"
            onClick={async () => {
              await logout();
              navigate("/");
            }}
            className="mt-3 flex items-center gap-2 text-xs text-muted-foreground hover:text-destructive transition-colors duration-150"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 border-b border-border sticky top-0 z-30 glass flex items-center justify-between px-4 lg:px-8">
          <button data-testid="mobile-menu-btn" className="lg:hidden" onClick={() => setOpen(!open)}>
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <span className="hidden lg:block overline text-muted-foreground">
            DevOps &amp; Cloud Career Track
          </span>
          <div className="flex items-center gap-3">
            <ThemeToggle />
          </div>
        </header>
        <TrialBanner />
        {open && (
          <div className="lg:hidden border-b border-border bg-background">
            {links("-mobile")}
            <button
              data-testid="logout-btn-mobile"
              onClick={async () => {
                await logout();
                navigate("/");
              }}
              className="flex items-center gap-2 px-4 py-3 text-sm text-destructive"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        )}
        <main className="flex-1 px-4 lg:px-8 py-8 max-w-[1400px] w-full">{children}</main>      </div>
    </div>
  );
};
